import { useEffect, useState } from "react";
import { ReceiptText, RotateCcw, Wallet, ShoppingCart, Boxes, ArrowRight, Tag, BadgeCheck, ChevronRight, ClipboardCheck, Handshake, Truck } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import adminApi from "@/lib/adminApi";

const money = (n) => (n == null ? "—" : `$${Number(n).toFixed(0)}`);
const fmtDate = (s) => (s || "").slice(0, 10) || "—";

const STATUS_STYLE = {
  "TRADE-IN REALIZADO": "bg-emerald-50 text-emerald-700 border-emerald-200",
  "CASH REALIZADO": "bg-emerald-50 text-emerald-700 border-emerald-200",
  "NO REALIZADO": "bg-rose-50 text-rose-700 border-rose-200",
};

const QUICK = [
  { id: "manual-tradein", label: "Hacer Trade-In", Icon: RotateCcw },
  { id: "simple-sale", label: "Venta simple", Icon: ShoppingCart },
  { id: "inventory", label: "iPhone Usados", Icon: Boxes },
  { id: "purchases", label: "Compra directa", Icon: Wallet },
  // Precios de mercado: Admin abre la pantalla editable ("prices"); vendedor/manager la de SOLO lectura ("reference").
  { id: "prices", label: "Precios de mercado", Icon: Tag, targets: ["prices", "reference"] },
];

const fmt = (iso) => { try { return new Date(iso).toLocaleDateString("es", { day: "2-digit", month: "short" }); } catch { return ""; } };

export const HomeDashboard = ({ onNavigate, allowed }) => {
  const [kpis, setKpis] = useState(null);
  const [recent, setRecent] = useState(null);
  const [alerts, setAlerts] = useState(null);
  const [apprCount, setApprCount] = useState(0);
  const [modal, setModal] = useState(null); // 'money' | 'settlement'

  useEffect(() => {
    adminApi.get("/admin/reports", { params: { period: "this_month" } })
      .then((r) => setKpis(r.data)).catch(() => setKpis(false));
    adminApi.get("/admin/quotes").then((r) => {
      const arr = Array.isArray(r.data) ? [...r.data] : [];
      arr.sort((a, b) => String(b.created_at || "").localeCompare(String(a.created_at || "")));
      setRecent(arr.slice(0, 5));
    }).catch(() => setRecent(false));
    adminApi.get("/admin/alerts/pending").then((r) => setAlerts(r.data)).catch(() => setAlerts(false));
    if (!allowed || allowed.has("approvals")) {
      adminApi.get("/admin/approvals/count").then((r) => setApprCount(r.data?.pending || 0)).catch(() => {});
    }
  }, [allowed]);

  const refreshAlerts = () => adminApi.get("/admin/alerts/pending").then((r) => setAlerts(r.data)).catch(() => {});
  const doMoney = async (m, action) => {
    if (action === "approve") {
      const label = m.kind === "investor" ? "marcar como PAGADA" : "confirmar";
      if (!window.confirm(`¿Seguro que deseas ${label} la solicitud de ${m.owner_name || "—"} por ${money(m.amount)}?`)) return;
      try {
        if (m.kind === "investor") await adminApi.post(`/admin/withdrawal-requests/${m.id}/pay`, {});
        else await adminApi.post(`/admin/payments/${m.id}/confirm`, {});
        toast.success("Solicitud procesada"); refreshAlerts();
      } catch (e) { toast.error(e?.response?.data?.detail || "No se pudo procesar"); }
    } else {
      const reason = window.prompt("Motivo de la cancelación:");
      if (reason === null) return;
      try {
        if (m.kind === "investor") await adminApi.post(`/admin/withdrawal-requests/${m.id}/cancel`, {});
        else await adminApi.post(`/admin/payments/${m.id}/cancel`, { reason });
        toast.success("Solicitud cancelada"); refreshAlerts();
      } catch (e) { toast.error(e?.response?.data?.detail || "No se pudo cancelar"); }
    }
  };
  const doSettle = async (s, action) => {
    if (action === "approve") {
      if (!window.confirm(`¿Aprobar la liquidación de ${s.owner_name || "—"}? Consignador ${money(s.proposed_owner_amount)} · PuntoCell ${money(s.proposed_pc)}`)) return;
      try { await adminApi.post(`/admin/new-iphones/${s.new_id}/approve-settlement`, {}); toast.success("Liquidación aprobada"); refreshAlerts(); }
      catch (e) { toast.error(e?.response?.data?.detail || "No se pudo aprobar"); }
    } else {
      const reason = window.prompt("Motivo del rechazo:");
      if (!reason) return;
      try { await adminApi.post(`/admin/new-iphones/${s.new_id}/reject-settlement`, { reason }); toast.success("Propuesta rechazada"); refreshAlerts(); }
      catch (e) { toast.error(e?.response?.data?.detail || "No se pudo rechazar"); }
    }
  };

  const cards = kpis && kpis !== false ? [
    { label: "Cotizaciones", value: kpis.total ?? 0, Icon: ReceiptText },
    { label: "Trade-In realizados", value: kpis.trade_in_done ?? 0, Icon: RotateCcw },
    { label: "Ventas (efectivo)", value: kpis.cash_done ?? 0, Icon: Wallet },
    { label: "Compra directa", value: kpis.direct_purchases?.total ?? 0, Icon: ShoppingCart },
  ] : [];

  const quick = QUICK
    .map((q) => {
      const targets = q.targets || [q.id];
      const target = targets.find((t) => !allowed || allowed.has(t));
      return target ? { ...q, target } : null;
    })
    .filter(Boolean);

  return (
    <div data-testid="home-dashboard" className="space-y-8">
      <div>
        <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight text-zinc-900">Panel de control</h1>
        <p className="text-sm text-zinc-500 mt-1">Resumen y operaciones</p>
      </div>

      {cards.length > 0 && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="home-kpis">
          {cards.map((c) => (
            <div key={c.label} className="rounded-2xl border border-zinc-200 bg-white p-4 shadow-sm" data-testid={`kpi-${c.label}`}>
              <div className="flex items-center gap-2 text-zinc-400"><c.Icon className="w-4 h-4" /><span className="text-[13px] font-medium">{c.label}</span></div>
              <p className="mt-2 text-3xl font-semibold tabular-nums text-zinc-900">{c.value}</p>
              <p className="text-[11px] text-zinc-400 mt-0.5">Este mes</p>
            </div>
          ))}
        </div>
      )}

      {(allowed?.has("approvals") || apprCount > 0) && (
        <div data-testid="home-approvals-alert">
          <div className="grid grid-cols-1 gap-3">
            {(() => { const n = apprCount; const active = n > 0; return (
              <button type="button" data-testid="alert-approvals-card" onClick={() => onNavigate("approvals")}
                className={`flex items-center justify-between gap-3 rounded-2xl border p-4 text-left transition-colors ${active ? "border-indigo-300 bg-indigo-50 hover:bg-indigo-100/70" : "border-zinc-200 bg-white hover:bg-zinc-50"}`}>
                <span className="flex items-center gap-3 min-w-0">
                  <span className={`grid h-11 w-11 shrink-0 place-items-center rounded-xl ${active ? "bg-indigo-200/70 text-indigo-800" : "bg-zinc-100 text-zinc-500"}`}><ClipboardCheck className="w-5 h-5" /></span>
                  <span className="min-w-0">
                    <span className={`block font-semibold ${active ? "text-indigo-900" : "text-zinc-800"}`}>Operaciones por aprobar</span>
                    <span className="block text-xs text-zinc-500">Ventas y Trade-In de vendedores</span>
                    <span data-testid="alert-approvals-count" className={`block text-sm font-semibold mt-0.5 ${active ? "text-indigo-800" : "text-zinc-400"}`}>{active ? `${n} operación${n === 1 ? "" : "es"} pendiente${n === 1 ? "" : "s"}` : "Sin pendientes"}</span>
                  </span>
                </span>
                <ChevronRight className={`w-5 h-5 shrink-0 ${active ? "text-indigo-400" : "text-zinc-300"}`} />
              </button>
            ); })()}
          </div>
        </div>
      )}

      {alerts && alerts !== false && (
        <div data-testid="home-alerts">
          <h2 className="text-lg font-semibold text-zinc-900 mb-3">Acciones rápidas</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
            {alerts.cash_supported && (() => { const n = alerts.money_count || 0; const active = n > 0; return (
              <button type="button" data-testid="alert-money-card" onClick={() => setModal("money")}
                className={`flex items-center justify-between gap-3 rounded-2xl border p-4 text-left transition-colors ${active ? "border-orange-300 bg-orange-50 hover:bg-orange-100/70" : "border-zinc-200 bg-white hover:bg-zinc-50"}`}>
                <span className="flex items-center gap-3 min-w-0">
                  <span className={`grid h-11 w-11 shrink-0 place-items-center rounded-xl ${active ? "bg-orange-200/70 text-orange-800" : "bg-zinc-100 text-zinc-500"}`}><Wallet className="w-5 h-5" /></span>
                  <span className="min-w-0">
                    <span className={`block font-semibold ${active ? "text-orange-900" : "text-zinc-800"}`}>Solicitudes de dinero</span>
                    <span className="block text-xs text-zinc-500">Consignadores e inversionistas</span>
                    <span data-testid="alert-money-count" className={`block text-sm font-semibold mt-0.5 ${active ? "text-orange-800" : "text-zinc-400"}`}>{active ? `${n} solicitud${n === 1 ? "" : "es"} pendiente${n === 1 ? "" : "s"}` : "Sin pendientes"}</span>
                  </span>
                </span>
                <ChevronRight className={`w-5 h-5 shrink-0 ${active ? "text-orange-400" : "text-zinc-300"}`} />
              </button>
            ); })()}
            {alerts.cash_supported && (() => { const n = alerts.settlement_count || 0; const active = n > 0; return (
              <button type="button" data-testid="alert-settlement-card" onClick={() => setModal("settlement")}
                className={`flex items-center justify-between gap-3 rounded-2xl border p-4 text-left transition-colors ${active ? "border-emerald-300 bg-emerald-50 hover:bg-emerald-100/70" : "border-zinc-200 bg-white hover:bg-zinc-50"}`}>
                <span className="flex items-center gap-3 min-w-0">
                  <span className={`grid h-11 w-11 shrink-0 place-items-center rounded-xl ${active ? "bg-emerald-200/70 text-emerald-800" : "bg-zinc-100 text-zinc-500"}`}><BadgeCheck className="w-5 h-5" /></span>
                  <span className="min-w-0">
                    <span className={`block font-semibold ${active ? "text-emerald-900" : "text-zinc-800"}`}>Liquidaciones por aprobar</span>
                    <span className="block text-xs text-zinc-500">Propuestas de consignadores</span>
                    <span data-testid="alert-settlement-count" className={`block text-sm font-semibold mt-0.5 ${active ? "text-emerald-800" : "text-zinc-400"}`}>{active ? `${n} liquidación${n === 1 ? "" : "es"} pendiente${n === 1 ? "" : "s"}` : "Sin pendientes"}</span>
                  </span>
                </span>
                <ChevronRight className={`w-5 h-5 shrink-0 ${active ? "text-emerald-400" : "text-zinc-300"}`} />
              </button>
            ); })()}
            {alerts.withdrawals_supported && (() => { const n = alerts.consignor_sales_count || 0; const active = n > 0; return (
              <button type="button" data-testid="alert-consignor-sales-card" onClick={() => setModal("consignor_sales")}
                className={`flex items-center justify-between gap-3 rounded-2xl border p-4 text-left transition-colors ${active ? "border-sky-300 bg-sky-50 hover:bg-sky-100/70" : "border-zinc-200 bg-white hover:bg-zinc-50"}`}>
                <span className="flex items-center gap-3 min-w-0">
                  <span className={`grid h-11 w-11 shrink-0 place-items-center rounded-xl ${active ? "bg-sky-200/70 text-sky-800" : "bg-zinc-100 text-zinc-500"}`}><Handshake className="w-5 h-5" /></span>
                  <span className="min-w-0">
                    <span className={`block font-semibold ${active ? "text-sky-900" : "text-zinc-800"}`}>Ventas de consignadores</span>
                    <span className="block text-xs text-zinc-500">Gestionadas desde su portal</span>
                    <span data-testid="alert-consignor-sales-count" className={`block text-sm font-semibold mt-0.5 ${active ? "text-sky-800" : "text-zinc-400"}`}>{active ? `${n} por procesar` : "Sin pendientes"}</span>
                  </span>
                </span>
                <ChevronRight className={`w-5 h-5 shrink-0 ${active ? "text-sky-400" : "text-zinc-300"}`} />
              </button>
            ); })()}
            {alerts.withdrawals_supported && (() => { const n = alerts.merch_count || 0; const active = n > 0; return (
              <button type="button" data-testid="alert-merch-card" onClick={() => setModal("merch")}
                className={`flex items-center justify-between gap-3 rounded-2xl border p-4 text-left transition-colors ${active ? "border-amber-300 bg-amber-50 hover:bg-amber-100/70" : "border-zinc-200 bg-white hover:bg-zinc-50"}`}>
                <span className="flex items-center gap-3 min-w-0">
                  <span className={`grid h-11 w-11 shrink-0 place-items-center rounded-xl ${active ? "bg-amber-200/70 text-amber-800" : "bg-zinc-100 text-zinc-500"}`}><Truck className="w-5 h-5" /></span>
                  <span className="min-w-0">
                    <span className={`block font-semibold ${active ? "text-amber-900" : "text-zinc-800"}`}>Retiros de mercancía</span>
                    <span className="block text-xs text-zinc-500">Entregas solicitadas por consignadores</span>
                    <span data-testid="alert-merch-count" className={`block text-sm font-semibold mt-0.5 ${active ? "text-amber-800" : "text-zinc-400"}`}>{active ? `${n} pendiente${n === 1 ? "" : "s"}` : "Sin pendientes"}</span>
                  </span>
                </span>
                <ChevronRight className={`w-5 h-5 shrink-0 ${active ? "text-amber-400" : "text-zinc-300"}`} />
              </button>
            ); })()}
          </div>
        </div>
      )}

      {quick.length > 0 && (
        <div>
          <h2 className="text-lg font-semibold text-zinc-900 mb-3">{alerts && alerts !== false ? "Operaciones" : "Acciones rápidas"}</h2>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="home-quick-actions">
            {quick.map(({ id, label, Icon, target }) => (
              <button key={id} type="button" onClick={() => onNavigate(target)} data-testid={`home-action-${id}`}
                className="group flex items-center justify-between gap-3 rounded-2xl border border-zinc-200 bg-white p-4 text-left shadow-sm transition-colors hover:border-zinc-900 hover:bg-zinc-900 hover:text-white">
                <span className="flex items-center gap-3 font-medium">
                  <span className="grid h-10 w-10 place-items-center rounded-xl bg-zinc-100 text-zinc-700 group-hover:bg-white/15 group-hover:text-white"><Icon className="w-5 h-5" /></span>
                  {label}
                </span>
                <ArrowRight className="w-4 h-4 text-zinc-300 group-hover:text-white" />
              </button>
            ))}
          </div>
        </div>
      )}

      {Array.isArray(recent) && recent.length > 0 && (
        <div>
          <h2 className="text-lg font-semibold text-zinc-900 mb-3">Actividad reciente</h2>
          <div className="rounded-2xl border border-zinc-200 bg-white divide-y divide-zinc-100 shadow-sm" data-testid="home-recent">
            {recent.map((q) => (
              <button key={q.id} type="button" onClick={() => onNavigate("quotes")} className="flex w-full items-center justify-between gap-3 px-4 py-3 text-left hover:bg-zinc-50">
                <div className="min-w-0">
                  <p className="font-medium text-zinc-900 truncate">{q.model} <span className="font-normal text-zinc-400">{q.capacity}</span></p>
                  <p className="text-xs text-zinc-400">{q.code || q.id} · {fmt(q.created_at)}</p>
                </div>
                {q.operation_status && <span className={`shrink-0 rounded-full border px-2 py-0.5 text-[11px] font-semibold ${STATUS_STYLE[q.operation_status] || "bg-zinc-100 text-zinc-600 border-zinc-200"}`}>{q.operation_status}</span>}
              </button>
            ))}
          </div>
        </div>
      )}

      <Dialog open={!!modal} onOpenChange={(o) => !o && setModal(null)}>
        <DialogContent className="rounded-3xl w-[calc(100vw-1.5rem)] sm:w-full max-w-lg max-h-[85vh] overflow-y-auto" data-testid="alert-modal">
          <DialogHeader>
            <DialogTitle>{modal === "money" ? "Solicitudes de dinero" : modal === "settlement" ? "Liquidaciones por aprobar" : modal === "consignor_sales" ? "Ventas de consignadores" : "Retiros de mercancía"}</DialogTitle>
            <DialogDescription>{modal === "money" ? "Solicitudes de retiro pendientes de consignadores e inversionistas." : modal === "settlement" ? "Propuestas POST_SALE enviadas por consignadores, pendientes de tu aprobación." : modal === "consignor_sales" ? "Ventas gestionadas por el consignador desde su portal, pendientes de procesar." : "Entregas/retiros de mercancía solicitados por consignadores, pendientes de confirmar."}</DialogDescription>
          </DialogHeader>
          {modal === "money" && alerts && alerts !== false && (
            <div className="space-y-2">
              {(alerts.money_requests || []).length === 0 ? <p className="text-sm text-zinc-400 py-8 text-center" data-testid="alert-money-empty">Sin solicitudes pendientes.</p> : (alerts.money_requests || []).map((m) => (
                <div key={m.id} data-testid={`alert-money-row-${m.id}`} className="rounded-xl border border-zinc-200 p-3 text-sm">
                  <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
                    <div className="min-w-0">
                      <p className="font-semibold text-zinc-900 break-words">{m.owner_name || "—"} <span className="font-normal text-zinc-400">{m.owner_code ? `(${m.owner_code})` : ""}</span></p>
                      <p className="text-xs text-zinc-500 break-words">Solicitó: {m.requested_by || "—"}{m.method ? ` · ${m.method}` : ""} · {fmtDate(m.date)}</p>
                    </div>
                    <div className="flex items-center gap-2 sm:flex-col sm:items-end sm:text-right shrink-0">
                      <p className="font-bold text-orange-700">{money(m.amount)}</p>
                      <span className="text-[10px] font-semibold rounded-full bg-orange-100 text-orange-700 px-2 py-0.5">{m.status}</span>
                    </div>
                  </div>
                  <div className="mt-2 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                    <span className="text-[10px] font-semibold rounded-full bg-zinc-100 text-zinc-600 px-2 py-0.5 self-start">{m.kind === "investor" ? "Inversionista" : "Consignador"}</span>
                    <div className="grid grid-cols-2 gap-2 sm:flex sm:gap-1.5">
                      <button type="button" data-testid={`alert-money-approve-${m.id}`} onClick={() => doMoney(m, "approve")} className="h-9 px-3 rounded-lg bg-emerald-600 text-white text-xs font-medium">{m.kind === "investor" ? "Marcar pagada" : "Confirmar"}</button>
                      <button type="button" data-testid={`alert-money-cancel-${m.id}`} onClick={() => doMoney(m, "cancel")} className="h-9 px-3 rounded-lg border border-zinc-300 text-zinc-700 text-xs font-medium">Cancelar</button>
                    </div>
                  </div>
                </div>
              ))}
              <p className="text-[11px] text-zinc-400 pt-1" data-testid="alert-investor-note">Aquí se muestran las solicitudes de retiro pendientes de consignadores e inversionistas.</p>
              {(() => {
                const mr = alerts.money_requests || [];
                const hasInv = mr.some((m) => m.kind === "investor");
                const hasCons = mr.some((m) => m.kind !== "investor");
                const showCons = hasCons || (!hasInv && !hasCons);
                return (
                  <div className="flex flex-col gap-2">
                    {showCons && (
                      <button type="button" data-testid="alert-money-goto" onClick={() => { setModal(null); onNavigate("consignadores"); }} className="w-full h-11 rounded-xl bg-zinc-900 text-white text-sm font-medium inline-flex items-center justify-center gap-2">Abrir en Consignadores <ArrowRight className="w-4 h-4" /></button>
                    )}
                    {hasInv && (
                      <button type="button" data-testid="alert-money-goto-investor" onClick={() => { setModal(null); onNavigate("investments"); }} className="w-full h-11 rounded-xl bg-zinc-900 text-white text-sm font-medium inline-flex items-center justify-center gap-2">Abrir en Inversionistas <ArrowRight className="w-4 h-4" /></button>
                    )}
                  </div>
                );
              })()}
            </div>
          )}
          {modal === "settlement" && alerts && alerts !== false && (
            <div className="space-y-2">
              {(alerts.settlements || []).length === 0 ? <p className="text-sm text-zinc-400 py-8 text-center" data-testid="alert-settlement-empty">Sin liquidaciones pendientes.</p> : (alerts.settlements || []).map((s) => (
                <div key={s.new_id} data-testid={`alert-settlement-row-${s.new_id}`} className="rounded-xl border border-zinc-200 p-3 text-sm space-y-1">
                  <div className="flex items-start justify-between gap-2">
                    <p className="font-semibold text-zinc-900 truncate">{s.model} <span className="font-normal text-zinc-400">{s.capacity || ""}</span></p>
                    <span className="text-[10px] font-mono text-zinc-400 shrink-0">{s.new_id}</span>
                  </div>
                  <p className="text-xs text-zinc-500 truncate">{s.owner_name || "—"} {s.owner_code ? `(${s.owner_code})` : ""} · {fmtDate(s.proposed_at)}</p>
                  <div className="flex flex-wrap gap-x-4 gap-y-0.5 text-xs">
                    <span className="text-zinc-500">Vendido: <b className="text-zinc-800">{money(s.sold_price)}</b></span>
                    <span className="text-zinc-500">Pide: <b className="text-emerald-700">{money(s.proposed_owner_amount)}</b></span>
                    <span className="text-zinc-500">PuntoCell: <b className="text-zinc-800">{money(s.proposed_pc)}</b></span>
                  </div>
                  <div className="flex gap-1.5 pt-1">
                    <button type="button" data-testid={`alert-settle-approve-${s.new_id}`} onClick={() => doSettle(s, "approve")} className="h-8 px-3 rounded-lg bg-emerald-600 text-white text-xs font-medium">Aprobar</button>
                    <button type="button" data-testid={`alert-settle-reject-${s.new_id}`} onClick={() => doSettle(s, "reject")} className="h-8 px-3 rounded-lg border border-zinc-300 text-zinc-700 text-xs font-medium">Rechazar</button>
                  </div>
                </div>
              ))}
              <button type="button" data-testid="alert-settlement-goto" onClick={() => { setModal(null); onNavigate("consignadores"); }} className="w-full h-11 rounded-xl bg-zinc-900 text-white text-sm font-medium inline-flex items-center justify-center gap-2">Abrir en Consignadores <ArrowRight className="w-4 h-4" /></button>
            </div>
          )}
          {(modal === "consignor_sales" || modal === "merch") && alerts && alerts !== false && (() => {
            const list = modal === "consignor_sales" ? (alerts.consignor_sales || []) : (alerts.merch_withdrawals || []);
            const typeLabel = { VENTA_LUIS_COBRAR: "Venta · por cobrar", VENTA_LUIS_PAGADA: "Venta · ya pagada", ENTREGA_SIN_COBRO: "Entrega sin cobro", OTRO: "Otro" };
            return (
              <div className="space-y-2">
                {list.length === 0 ? <p className="text-sm text-zinc-400 py-8 text-center" data-testid={`alert-${modal}-empty`}>Sin solicitudes pendientes.</p> : list.map((w) => (
                  <div key={w.id} data-testid={`alert-${modal}-row-${w.id}`} className="rounded-xl border border-zinc-200 p-3 text-sm space-y-1">
                    <div className="flex items-start justify-between gap-2">
                      <p className="font-semibold text-zinc-900 truncate">{w.owner_name || "—"} <span className="font-normal text-zinc-400">{w.owner_code ? `(${w.owner_code})` : ""}</span></p>
                      <span className="text-[10px] font-mono text-zinc-400 shrink-0">{w.id}</span>
                    </div>
                    <p className="text-xs text-zinc-600 truncate">{w.item_label || "—"}{w.qty ? ` ×${w.qty}` : ""}{w.imei ? ` · IMEI ${w.imei}` : ""}</p>
                    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs">
                      <span className="text-[10px] font-semibold rounded-full bg-zinc-100 text-zinc-600 px-2 py-0.5">{typeLabel[w.withdrawal_type] || w.withdrawal_type}</span>
                      {w.pickup_name && <span className="text-zinc-500">Retira: <b className="text-zinc-800">{w.pickup_name}</b></span>}
                      {w.amount_to_collect != null && <span className="text-zinc-500">Cobrar: <b className="text-orange-700">{money(w.amount_to_collect)}</b></span>}
                      <span className="text-zinc-400">{fmtDate(w.date)}</span>
                    </div>
                    {w.notes && <p className="text-xs text-zinc-500 border-l-2 border-zinc-200 pl-2">{w.notes}</p>}
                  </div>
                ))}
                <button type="button" data-testid={`alert-${modal}-goto`} onClick={() => { setModal(null); onNavigate("withdrawals"); }} className="w-full h-11 rounded-xl bg-zinc-900 text-white text-sm font-medium inline-flex items-center justify-center gap-2">Gestionar en Retiros <ArrowRight className="w-4 h-4" /></button>
              </div>
            );
          })()}
        </DialogContent>
      </Dialog>
    </div>
  );
};
